import { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || (typeof window !== 'undefined' ? window.location.origin : '');
const traderAPI = axios.create({baseURL: API_URL});
traderAPI.interceptors.request.use(config => {
  const token=localStorage.getItem('trader_token');
  if(token&&!config.headers.Authorization)config.headers.Authorization=`Bearer ${token}`;
  return config;
});
const TraderAuthContext=createContext(null);
const apiMessage=error=>{
  const detail=error.response?.data?.detail;
  return typeof detail==='string'?detail:Array.isArray(detail)?detail.map(e=>e.msg).join('; '):'Request failed. Please try again.';
};

export const TraderAuthProvider=({children})=>{
  const [user,setUser]=useState(null);
  const [token,setToken]=useState(()=>localStorage.getItem('trader_token'));
  const [loading,setLoading]=useState(true);
  const generation=useRef(0), pending=useRef(null);
  const logout=useCallback(()=>{
    generation.current+=1;
    pending.current?.controller.abort();pending.current=null;
    localStorage.removeItem('trader_token');localStorage.removeItem('trader_user');
    setToken(null);setUser(null);setLoading(false);
  },[]);
  useEffect(()=>{
    const revoked=event=>{if(event.detail?.token===localStorage.getItem('trader_token'))logout();};
    window.addEventListener('trader-session-revoked',revoked);
    return()=>window.removeEventListener('trader-session-revoked',revoked);
  },[logout]);
  const refreshUser=useCallback(async()=>{
    const currentToken=localStorage.getItem('trader_token');
    if(!currentToken)return null;
    if(pending.current?.token===currentToken&&!pending.current.controller.signal.aborted)return pending.current.promise;
    pending.current?.controller.abort();
    const epoch=generation.current,controller=new AbortController();
    const request={token:currentToken,controller};
    request.promise=traderAPI.get('/api/trader/me',{headers:{Authorization:`Bearer ${currentToken}`},signal:controller.signal,timeout:20000}).then(({data})=>{
      if(epoch!==generation.current||localStorage.getItem('trader_token')!==currentToken)return null;
      setUser(data);localStorage.setItem('trader_user',JSON.stringify(data));return data;
    }).catch(error=>{
      if(epoch===generation.current&&localStorage.getItem('trader_token')===currentToken&&error.response?.status===401)logout();
      return null;
    }).finally(()=>{if(pending.current===request)pending.current=null;});
    pending.current=request;
    return request.promise;
  },[logout]);

  const authenticate=useCallback(async(path,body)=>{
    const epoch=++generation.current;
    pending.current?.controller.abort();pending.current=null;
    try{
      const {data}=await traderAPI.post(path,body);
      if(epoch!==generation.current)return {success:false,error:'Session changed. Please sign in again.'};
      localStorage.setItem('trader_token',data.token);localStorage.setItem('trader_user',JSON.stringify(data.user));
      setToken(data.token);setUser(data.user);setLoading(false);
      return {success:true,user:data.user};
    }catch(error){return {success:false,error:apiMessage(error)};}
  },[]);
  const login=useCallback((email,password)=>authenticate('/api/trader/auth/login',{email,password}),[authenticate]);
  const register=useCallback(data=>authenticate('/api/trader/auth/register',data),[authenticate]);

  useEffect(()=>{
    let active=true;
    if(!token){setLoading(false);return;}
    refreshUser().finally(()=>{if(active)setLoading(false);});
    const interval=setInterval(refreshUser,10000);
    const onFocus=()=>{if(document.visibilityState==='visible')refreshUser();};
    window.addEventListener('focus',onFocus);
    window.addEventListener('online',onFocus);
    document.addEventListener('visibilitychange',onFocus);
    window.addEventListener('trader-data-changed',refreshUser);
    return()=>{active=false;clearInterval(interval);window.removeEventListener('focus',onFocus);window.removeEventListener('online',onFocus);document.removeEventListener('visibilitychange',onFocus);window.removeEventListener('trader-data-changed',refreshUser);};
  },[token,refreshUser]);
  useEffect(()=>()=>{generation.current+=1;pending.current?.controller.abort();},[]);
  useEffect(()=>{
    const synchronize=event=>{
      if(event.key!=='trader_token')return;
      generation.current+=1;pending.current?.controller.abort();pending.current=null;
      setUser(null);setToken(event.newValue);setLoading(Boolean(event.newValue));
    };
    window.addEventListener('storage',synchronize);
    return()=>window.removeEventListener('storage',synchronize);
  },[]);
  return <TraderAuthContext.Provider value={{user,loading,login,register,logout,refreshUser,isAuthenticated:!!user}}>{children}</TraderAuthContext.Provider>;
};
export const useTraderAuth=()=>{
  const context=useContext(TraderAuthContext);
  if(!context)throw new Error('useTraderAuth must be used within TraderAuthProvider');
  return context;
};
export {traderAPI};